import { useState, useEffect, useRef, useCallback, useMemo } from "react";
import { IWidget } from "@pages/Dashboard/types/dashboard.types";
import { widgetService } from "@services/widget.service";
import { endOfWeek, startOfWeek, subWeeks } from "date-fns";
import { WidgetMetricKey } from "@services/math/math.service";
import isEqual from "lodash/isEqual";

interface UseWidgetsProps {
  selectedWidgets: WidgetMetricKey[];
  dateRange: { startDate: Date; endDate: Date };
  compareWith: { startDate: Date; endDate: Date };
  compareType: "raw" | "percent";
  showHistorical?: boolean;
  isWhatIf?: boolean;
  saturation?: any;
  campaignsTab?: string;
  saturationKeys?: any;
  historicalShowGroup?: string[];
  asin?: string;
}

export const useWidgets = ({
  selectedWidgets,
  dateRange,
  compareWith,
  compareType,
  showHistorical = false,
  isWhatIf = false,
  saturation,
  campaignsTab,
  saturationKeys,
  historicalShowGroup = [],
  asin,
}: UseWidgetsProps) => {
  const [widgets, setWidgets] = useState<IWidget[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);

  const isLoadingRef = useRef(false);

  const fetchIdRef = useRef(0);

  const memoizedSelectedWidgets = useMemo(() => {
    return selectedWidgets;
  }, [JSON.stringify(selectedWidgets)]);

  const memoizedDateRange = useMemo(() => {
    return {
      startDate: dateRange.startDate,
      endDate: dateRange.endDate,
    };
  }, [dateRange.startDate.getTime(), dateRange.endDate.getTime()]);

  const memoizedCompareWith = useMemo(() => {
    return {
      startDate: compareWith.startDate,
      endDate: compareWith.endDate,
    };
  }, [compareWith.startDate.getTime(), compareWith.endDate.getTime()]);

  const memoizedSaturation = useMemo(
    () => saturation,
    [JSON.stringify(saturation)],
  );
  const memoizedSaturationKeys = useMemo(
    () => saturationKeys,
    [JSON.stringify(saturationKeys)],
  );
  const memoizedHistoricalGroup = useMemo(
    () => historicalShowGroup,
    [JSON.stringify(historicalShowGroup)],
  );

  const prevProps = useRef({
    selectedWidgets: memoizedSelectedWidgets,
    dateRange: memoizedDateRange,
    compareWith: memoizedCompareWith,
    compareType,
    showHistorical,
    isWhatIf,
    saturation: memoizedSaturation,
    campaignsTab,
    saturationKeys: memoizedSaturationKeys,
    historicalShowGroup: memoizedHistoricalGroup,
    asin,
  });

  const shouldFetchData = useCallback(() => {
    const prev = prevProps.current;

    const selectedWidgetsChanged = !isEqual(
      prev.selectedWidgets,
      memoizedSelectedWidgets,
    );
    const dateRangeChanged =
      prev.dateRange.startDate.getTime() !==
        memoizedDateRange.startDate.getTime() ||
      prev.dateRange.endDate.getTime() !== memoizedDateRange.endDate.getTime();
    const compareWithChanged =
      prev.compareWith.startDate.getTime() !==
        memoizedCompareWith.startDate.getTime() ||
      prev.compareWith.endDate.getTime() !==
        memoizedCompareWith.endDate.getTime();

    const hasChanged =
      selectedWidgetsChanged ||
      dateRangeChanged ||
      compareWithChanged ||
      prev.compareType !== compareType ||
      prev.showHistorical !== showHistorical ||
      prev.isWhatIf !== isWhatIf ||
      !isEqual(prev.saturation, memoizedSaturation) ||
      prev.campaignsTab !== campaignsTab ||
      !isEqual(prev.saturationKeys, memoizedSaturationKeys) ||
      !isEqual(prev.historicalShowGroup, memoizedHistoricalGroup) ||
      prev.asin !== asin;

    if (hasChanged) {
      prevProps.current = {
        selectedWidgets: memoizedSelectedWidgets,
        dateRange: memoizedDateRange,
        compareWith: memoizedCompareWith,
        compareType,
        showHistorical,
        isWhatIf,
        saturation: memoizedSaturation,
        campaignsTab,
        saturationKeys: memoizedSaturationKeys,
        historicalShowGroup: memoizedHistoricalGroup,
        asin,
      };
    }

    return hasChanged;
  }, [
    memoizedSelectedWidgets,
    memoizedDateRange,
    memoizedCompareWith,
    compareType,
    showHistorical,
    isWhatIf,
    memoizedSaturation,
    campaignsTab,
    memoizedSaturationKeys,
    memoizedHistoricalGroup,
    asin,
  ]);

  useEffect(() => {
    if ((!shouldFetchData() && widgets.length > 0) || isLoadingRef.current) {
      return;
    }

    const fetchWidgets = async () => {
      const fetchId = ++fetchIdRef.current;

      if (!memoizedSelectedWidgets.length) {
        setWidgets([]);
        setIsLoading(false);
        return;
      }

      if (isLoadingRef.current) {
        console.log("Already loading, skipping fetch");
        return;
      }

      console.log("[useWidgets] Starting fetch with ID:", fetchId);
      isLoadingRef.current = true;
      setIsLoading(true);

      try {
        setError(null);

        const startDateString = memoizedDateRange.startDate
          .toISOString()
          .split("T")[0];
        const endDateString = memoizedDateRange.endDate
          .toISOString()
          .split("T")[0];

        const compareStartDate = memoizedCompareWith.startDate;
        const compareEndDate = memoizedCompareWith.endDate;
        const compareStartDateString = startOfWeek(
          subWeeks(compareStartDate, 1),
        )
          .toISOString()
          .split("T")[0];
        const compareEndDateString = endOfWeek(subWeeks(compareEndDate, 1))
          .toISOString()
          .split("T")[0];

        if (fetchId !== fetchIdRef.current) {
          console.log(
            `[useWidgets] Fetch ${fetchId} aborted, current is ${fetchIdRef.current}`,
          );
          return;
        }

        const response = await widgetService.getWidgetsData({
          selectedKeys: memoizedSelectedWidgets,
          dateStart: startDateString,
          dateEnd: endDateString,
          compareStart: compareStartDateString,
          compareEnd: compareEndDateString,
          compareType,
          includeChartData:
            showHistorical || memoizedHistoricalGroup.length > 0,
          isWhatIf,
          saturation: memoizedSaturation,
          saturationTab: campaignsTab,
          saturationKeys: memoizedSaturationKeys,
          asin,
        });

        console.log(response);
        setWidgets(response);

        if (fetchId === fetchIdRef.current) {
          console.log(`[useWidgets] Fetch ${fetchId} completed successfully`);
        } else {
          console.log(
            `[useWidgets] Fetch ${fetchId} completed but skipped (outdated)`,
          );
        }
      } catch (err) {
        console.error("[useWidgets] Error fetching widgets:", err);
        if (fetchId === fetchIdRef.current) {
          setError(
            err instanceof Error ? err : new Error("Failed to fetch widgets"),
          );
          setWidgets([]);
        }
      } finally {
        isLoadingRef.current = false;
        setIsLoading(false);
        if (fetchId === fetchIdRef.current) {
          console.log(`[useWidgets] Fetch ${fetchId} finalized`);
        }
      }
    };

    fetchWidgets();

    return () => {
      fetchIdRef.current++;
      console.log(`[useWidgets] Clean-up, new fetchId: ${fetchIdRef.current}`);
    };
  }, [shouldFetchData]);

  return { widgets, setWidgets, isLoading, error };
};
